import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/auth/auth.service';
import { InputGeneralComponent } from 'app/components/input-general/input-general.component';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import { CHANGE_LANGUAGE } from 'app/shared/utils';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'app-reset-password-settings',
    standalone: true,
    imports: [
        TranslateModule,
        FormsModule,
        ReactiveFormsModule,
        InputGeneralComponent,
        CommonModule,
    ],
    templateUrl: './reset-password-settings.component.html',
    styleUrl: './reset-password-settings.component.scss',
})
export class ResetPasswordSettingsComponent implements OnInit {
    passwordForm: any;
    isRequired: any;
    pswdStrength: any;
    digitOnlyError: any;
    isRequiredError: any;
    pswdStrengthLabel: any;
    pswdStrengthColor: any;
    loggedInUserData: any;
    invalidEmailError: any;
    passwordMatchError: any;
    passwordDigitError: any;
    phoneNoMinMaxError: any;
    passwordMinMaxError: any;
    passwordUppercaseError: any;
    passwordLowercaseError: any;
    passwordSpecialCharacterError: any;
    submitted: boolean = false;
    private storageChange = new BehaviorSubject<string | null>(
        localStorage.getItem('language')
    );
    storageChange$ = this.storageChange.asObservable();

    constructor(
        private formBuilder: FormBuilder,
        private translate: TranslateService,
        private authService: AuthService,
        private toastr: ToastrService,
        private router: Router,
        private _dashboardService: DashboardService
    ) {}

    ngOnInit(): void {
        this.passwordForm = this.formBuilder.group(
            {
                currentPassword: ['', Validators.required],
                newPassword: ['', Validators.required],
                confirmPassword: ['', Validators.required],
            },
            {
                validator: this.confirmPasswordValidation,
            }
        );

        this._dashboardService.language$.subscribe((value) => {
            this.switchLanguage(value);
        });

        this._translate();
    }

    _translate() {
        this.translate.get('ERROR.IS_REQUIRED').subscribe((data: any) => {
            this.isRequired = data;
            this.isRequiredError = data;
        });
        this.translate
            .get('ERROR.PASSWORD_MUST_MATCH')
            .subscribe((data: any) => {
                this.passwordMatchError = data;
            });
        this.translate
            .get('ERROR.PASSWORD_MIN_MAX_CHARACTERS')
            .subscribe((data: any) => {
                this.passwordMinMaxError = data;
            });
        this.translate
            .get('ERROR.PASSWORD_UPPERCASE')
            .subscribe((data: any) => {
                this.passwordUppercaseError = data;
            });

        this.translate
            .get('ERROR.PASSWORD_LOWERCASE')
            .subscribe((data: any) => {
                this.passwordLowercaseError = data;
            });

        this.translate.get('ERROR.PASSWORD_DIGIT').subscribe((data: any) => {
            this.passwordDigitError = data;
        });

        this.translate
            .get('ERROR.PASSWORD_SPECIAL_CHARACTER')
            .subscribe((data: any) => {
                this.passwordSpecialCharacterError = data;
            });
    }

    confirmPasswordValidation(control: AbstractControl) {
        const password = control.get('newPassword').value;
        const confirmPassword = control.get('confirmPassword').value;
        if (password && confirmPassword) {
            if (password !== confirmPassword) {
                control
                    .get('confirmPassword')
                    .setErrors({ MatchPassword: true });
            } else {
                control.get('confirmPassword').setErrors(null);
            }
        }
    }

    getErrorsPassword(input: any, inputName?: any): Array<any> {
        let errors = [];

        if (this.fPassword[input].touched || this.submitted) {
            if (this.fPassword[input].errors?.required) {
                errors.push(inputName + ' ' + this.isRequiredError);
            }
            if (
                input == 'newPassword' &&
                !this.fPassword[input].value?.match(
                    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,16}$/
                )
            ) {
                errors.push(
                    inputName + ' ' + this.passwordMinMaxError,
                    inputName + ' ' + this.passwordUppercaseError,
                    inputName + ' ' + this.passwordLowercaseError,
                    inputName + ' ' + this.passwordDigitError,
                    inputName + ' ' + this.passwordSpecialCharacterError
                );
            }
            if (this.fPassword[input].errors?.MatchPassword) {
                errors.push(this.passwordMatchError);
            }
            if (
                (input !== 'currentPassword' &&
                    this.fPassword[input].errors?.minlength) ||
                this.fPassword[input].errors?.maxlength
            ) {
                errors.push(inputName + ' ' + this.phoneNoMinMaxError);
            }
        }
        return errors;
    }

    get fPassword() {
        return this.passwordForm.controls;
    }

    disablePaste(event: any) {
        event.preventDefault();
    }

    checkPasswordStatus(event: any) {
        let password = event.target.value;
        if (
            password &&
            this.passwordForm.controls['newPassword'].value?.match(
                /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,16}$/
            )
        ) {
            if (password.length === 8) {
                this.pswdStrength = 1;
                this.pswdStrengthLabel = 'POOR';
                this.pswdStrengthColor = '#FF8819';
            } else if (password.length > 8 && password.length <= 11) {
                this.pswdStrength = 2;
                this.pswdStrengthLabel = 'FINE';
                this.pswdStrengthColor = '#FF8819';
            } else if (password.length > 11 && password.length <= 14) {
                this.pswdStrength = 3;
                this.pswdStrengthLabel = 'GOOD';
                this.pswdStrengthColor = '#4B6EEC';
            } else if (password.length > 14 && password.length <= 16) {
                this.pswdStrength = 4;
                this.pswdStrengthLabel = 'STRONG';
                this.pswdStrengthColor = '#4B6EEC';
            }
        } else {
            this.pswdStrength = 0;
            this.pswdStrengthLabel = '';
            this.pswdStrengthColor = '';
        }
    }

    onResetPassword() {
        this.submitted = true;
        if (this.passwordForm.valid) {
            let objReq = {
                prevPassword: this.fPassword['currentPassword'].value,
                newPassword: this.fPassword['newPassword'].value,
            };

            this.authService.updatePassword(objReq).subscribe({
                next: () => {
                    if (localStorage.getItem('language') == 'bm') {
                        this.toastr.success(
                            'Anda telah berjaya menetapkan semula kata laluan'
                        );
                    } else {
                        this.toastr.success(
                            'You have reset the password successfully'
                        );
                    }
                    this.fPassword['currentPassword'].setValue('');
                    this.fPassword['newPassword'].setValue('');
                    this.fPassword['confirmPassword'].setValue('');
                    this.passwordForm.markAsUntouched();
                    this.pswdStrength = 0;
                    this.pswdStrengthLabel = '';
                    this.pswdStrengthColor = '';
                    this.submitted = false;
                },
                error: (err) => {
                    let error = err.error.message;
                    if (error == 'Incorrect username or password.') {
                        if (localStorage.getItem('language') == 'bm') {
                            this.toastr.error(
                                'Butiran kata laluan lama tidak tepat'
                            );
                        } else {
                            this.toastr.error(error);
                        }
                    } else if (
                        error ==
                        'Attempt limit exceeded, please try after some time.'
                    ) {
                        if (localStorage.getItem('language') == 'bm') {
                            this.toastr.error(
                                'Percubaan telah melebihi had. Sila cuba lagi selepas beberapa ketika.'
                            );
                        } else {
                            this.toastr.error(error);
                        }
                    } else {
                        this.toastr.error(error);
                    }
                },
            });
        }
    }

    switchLanguage(language: string) {
        CHANGE_LANGUAGE(language, this.router.url, this.translate);
        this._translate();
    }
}
